import React, { useState, useEffect, useRef, useMemo } from 'react';

// How many cards to render per batch. The list can be thousands of items, so we
// render incrementally (infinite scroll) instead of mounting every card at once.
const PAGE_SIZE = 48;

// Variations are named "<product> - <colour>, <size>" (sometimes "<size>, <colour>",
// or a single part like "XL" or "EU 38 | US 7 | UK 5"). The POS stores no separate
// colour/size fields, so these read them back out of the name to group the cards
// by colour with sizes in order.
const LETTER_SIZES = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '4xl', '5xl', '6xl'];
const SIZE_ALIASES = {
  '2xl': 'xxl', '3xl': 'xxxl', small: 's', medium: 'm', large: 'l',
  'x-large': 'xl', xlarge: 'xl', 'xx-large': 'xxl', xxlarge: 'xxl',
};

const letterRank = (s) => {
  const k = s.toLowerCase().replace(/\s+/g, '');
  return LETTER_SIZES.indexOf(SIZE_ALIASES[k] || k);
};

const looksLikeSize = (s) =>
  letterRank(s) !== -1 ||
  /^\d/.test(s) ||
  /^(uk|eu|us)\s*\d/i.test(s) ||
  /\b(free|one)\s*size\b/i.test(s);

// Letter sizes in XS→XXL order, then anything with a number (38, 32W, UK 8,
// 2-3Y; baby sizes in months before years), then the rest alphabetically.
const sizeKey = (s) => {
  const rank = letterRank(s);
  if (rank !== -1) return [0, rank, ''];
  const m = s.match(/\d+(?:\.\d+)?/);
  if (m) {
    let n = parseFloat(m[0]);
    if (/^\d+(?:\s*-\s*\d+)?\s*m(?:onths?)?$/i.test(s)) n /= 12;
    return [1, n, s.toLowerCase()];
  }
  return [2, 0, s.toLowerCase()];
};

// Length of the shared "<product> - " part of the names, or 0 when the items
// don't share one (unrelated products), in which case there's nothing to parse.
const variantPrefixLength = (names) => {
  let prefix = names[0] || '';
  for (const name of names) {
    let i = 0;
    while (i < prefix.length && i < name.length && prefix[i] === name[i]) i += 1;
    prefix = prefix.slice(0, i);
    if (!prefix) return 0;
  }
  const cut = prefix.lastIndexOf(' - ');
  return cut === -1 ? 0 : cut + 3;
};

const parseVariant = (suffix) => {
  const parts = suffix.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 2) {
    const [a, b] = parts;
    if (looksLikeSize(a) && !looksLikeSize(b)) return { colour: b, size: a };
    return { colour: a, size: b };
  }
  if (parts.length === 1) {
    return looksLikeSize(parts[0]) ? { colour: '', size: parts[0] } : { colour: parts[0], size: '' };
  }
  return { colour: suffix.trim(), size: '' };
};

// Scanned item first, then the rest of its colour, then other colours A→Z,
// sizes in order within each.
// Falls back to "scanned item first, rest as returned" when names carry no
// shared product name to split on.
const orderByColourAndSize = (items, scannedBarcode) => {
  const cut = variantPrefixLength(items.map((item) => item.Name || ''));
  if (cut === 0) {
    const idx = items.findIndex((item) => item.Barcode === scannedBarcode);
    if (idx <= 0) return items;
    const reordered = items.slice();
    reordered.unshift(reordered.splice(idx, 1)[0]);
    return reordered;
  }
  const parsed = items.map((item) => {
    const { colour, size } = parseVariant((item.Name || '').slice(cut));
    return { item, colour: colour.toLowerCase(), size };
  });
  const scanned = parsed.find((p) => p.item.Barcode === scannedBarcode);
  const scannedColour = scanned ? scanned.colour : null;
  parsed.sort((a, b) => {
    if (a.colour !== b.colour) {
      if (a.colour === scannedColour) return -1;
      if (b.colour === scannedColour) return 1;
      return a.colour.localeCompare(b.colour);
    }
    const ka = sizeKey(a.size);
    const kb = sizeKey(b.size);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2].localeCompare(kb[2], undefined, { numeric: true });
  });
  // The exact match always leads; the rest of its colour follows in size order.
  if (scanned) {
    parsed.splice(parsed.indexOf(scanned), 1);
    parsed.unshift(scanned);
  }
  return parsed.map((p) => p.item);
};

const RelatedItemsDisplay = ({ items, scannedBarcode, isLoading, error }) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // The backend returns siblings in primary-key order, which reads as random.
  // Group them by colour (the scanned item's colour first, so it stays near the
  // top: only 48 cards render at a time) with sizes in order, so staff can see
  // at a glance which colours come in which sizes.
  const list = useMemo(() => {
    const base = items || [];
    if (base.length < 2) return base;
    return orderByColourAndSize(base, scannedBarcode);
  }, [items, scannedBarcode]);

  // Where the shared "<product> - " part of the names ends (0 = none), and each
  // item's colour, for bolding the variant part and the per-colour headers.
  const variantCut = useMemo(
    () => (list.length > 1 ? variantPrefixLength(list.map((item) => item.Name || '')) : 0),
    [list]
  );
  const colourOf = (item) =>
    variantCut > 0 ? parseVariant((item.Name || '').slice(variantCut)).colour : '';
  const colourGroups = useMemo(() => {
    const groups = new Map();
    if (variantCut === 0) return groups;
    for (const item of list) {
      const key = parseVariant((item.Name || '').slice(variantCut)).colour.toLowerCase();
      if (!key) continue;
      const g = groups.get(key) || { total: 0, inStore: 0 };
      g.total += 1;
      if (item.Stock > 0) g.inStore += 1;
      groups.set(key, g);
    }
    return groups;
  }, [list, variantCut]);

  // Reset the window whenever a new result set comes in.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [scannedBarcode, list]);

  // Reveal more as the sentinel nears the viewport (pre-loads ~600px early).
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((count) => (count < list.length ? count + PAGE_SIZE : count));
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [list]);

  // Summary counts over the WHOLE set (independent of how many cards are rendered).
  const summary = useMemo(() => ({
    storeIn: list.filter((item) => item.Stock > 0).length,
    storeOut: list.filter((item) => !(item.Stock > 0)).length,
    webAvailable: list.filter((item) => item.WebsiteStock > 0).length,
    webUnavailable: list.filter((item) => !(item.WebsiteStock > 0)).length,
  }), [list]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading related items...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-red-800 font-medium">Error loading items</span>
        </div>
        <p className="text-red-700 mt-1">{error}</p>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span className="text-yellow-800 font-medium">No related items found</span>
        </div>
        <p className="text-yellow-700 mt-1">This item has no related products.</p>
      </div>
    );
  }

  const visibleItems = list.slice(0, visibleCount);
  const hasMore = visibleCount < list.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">Related Items</h3>
            <p className="text-blue-700 text-sm">
              Found {list.length} related item{list.length !== 1 ? 's' : ''} for barcode: <span className="font-mono font-medium">{scannedBarcode}</span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-900">{list.length}</div>
            <div className="text-sm text-blue-600">items</div>
          </div>
        </div>
      </div>

      {/* Summary — moved to the top so the totals are visible without scrolling */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{summary.storeIn}</div>
            <div className="text-sm text-gray-600">Store In Stock</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{summary.storeOut}</div>
            <div className="text-sm text-gray-600">Store Out of Stock</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{summary.webAvailable}</div>
            <div className="text-sm text-gray-600">Website Available</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600">{summary.webUnavailable}</div>
            <div className="text-sm text-gray-600">Website Unavailable</div>
          </div>
        </div>
      </div>

      {/* Items list (rendered incrementally), grouped by colour */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_7rem_5.5rem] md:gap-4 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>Item</span>
          <span className="text-right">Store</span>
          <span className="text-right">Website</span>
          <span className="text-right">Price</span>
          <span className="text-right">Barcode</span>
        </div>
        {visibleItems.map((item, index) => {
          const isScannedItem = item.Barcode === scannedBarcode;
          const name = item.Name || 'Unnamed Item';
          const colour = colourOf(item);
          const startsGroup = colour && (index === 0 || colourOf(visibleItems[index - 1]).toLowerCase() !== colour.toLowerCase());
          const group = startsGroup ? colourGroups.get(colour.toLowerCase()) : null;
          const inStore = item.Stock > 0;
          const webStock = Number(item.WebsiteStock || 0);

          return (
            <React.Fragment key={item.ID ?? item.Barcode ?? index}>
              {startsGroup && (
                <div className="px-4 py-1.5 bg-gray-100 border-b border-gray-200 text-sm font-semibold text-gray-800">
                  {colour}
                  {group && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {group.inStore} of {group.total} in store
                    </span>
                  )}
                </div>
              )}
              <div
                className={`px-4 py-2.5 border-b border-gray-100 last:border-b-0 md:grid md:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_7rem_5.5rem] md:gap-4 md:items-center ${
                  isScannedItem
                    ? 'bg-green-50 border-l-4 border-l-green-500'
                    : inStore ? '' : 'bg-gray-50/60'
                }`}
              >
                {/* Full name, never clamped; the colour/size part is bolded since
                    it's what tells one row from the next. */}
                <div className="min-w-0">
                  <div className={`text-sm break-words ${inStore || isScannedItem ? 'text-gray-600' : 'text-gray-400'}`}>
                    {variantCut > 0 && name.length > variantCut ? (
                      <>
                        {name.slice(0, variantCut)}
                        <span className={`font-semibold ${inStore || isScannedItem ? 'text-gray-900' : 'text-gray-500'}`}>
                          {name.slice(variantCut)}
                        </span>
                      </>
                    ) : (
                      <span className="font-semibold text-gray-900">{name}</span>
                    )}
                    {isScannedItem && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 align-middle">
                        Scanned
                      </span>
                    )}
                  </div>
                  {item.NameUrdu && (
                    <p className="text-sm text-gray-500 mt-0.5 font-urdu">{item.NameUrdu}</p>
                  )}
                </div>

                {/* Phone: the numbers sit on one line under the name */}
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 md:hidden">
                  <span>Store <b className={inStore ? 'text-green-700' : 'text-red-500'}>{item.Stock || 0}</b></span>
                  <span>Web <b className={webStock > 0 ? 'text-blue-600' : 'text-gray-400'}>{webStock}</b></span>
                  <span>PKR {Number(item.SalePrice || 0).toLocaleString()}</span>
                  {item.Barcode && <span className="font-mono">{item.Barcode}</span>}
                </div>

                {/* Desktop columns */}
                <span className={`hidden md:block text-right font-semibold ${inStore ? 'text-green-700' : 'text-red-500'}`}>
                  {item.Stock || 0}
                </span>
                <span className={`hidden md:block text-right font-semibold ${webStock > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                  {webStock}
                </span>
                <span className="hidden md:block text-right text-sm text-gray-900">
                  PKR {Number(item.SalePrice || 0).toLocaleString()}
                </span>
                <span className="hidden md:block text-right font-mono text-sm text-gray-600">
                  {item.Barcode}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Infinite-scroll trigger + fallback control */}
      <div ref={sentinelRef} aria-hidden="true" />
      {hasMore ? (
        <div className="text-center">
          <button
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
          >
            Show more
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Showing {visibleItems.length} of {list.length} items
          </p>
        </div>
      ) : (
        list.length > PAGE_SIZE && (
          <p className="text-center text-xs text-gray-500">Showing all {list.length} items</p>
        )
      )}
    </div>
  );
};

export default RelatedItemsDisplay;
